"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getGaIds = exports.checkIfGaSessionIdNewer = exports.storeGaIds = void 0;
const ldLocalStorage_1 = require("./ldLocalStorage");
/**
 * Saves GA3 or GA4 clientID or sessionID to LD localStorage.
 * @param {string | undefined} id GA3 Property ID or GA4 Measurement ID
 * @param {GaIdType} type type of ID we want to retrieve (session or client)
 * @param {ILDTransactionMonitorPayload} payloadCallback ldPayload contents
 * @returns {Promise<void>} promise response saved to LD localStorage
 */
const storeGaIds = async (id, type, payloadCallback) => {
    const clientId = await (0, exports.getGaIds)(id, type);
    if (typeof clientId !== "string")
        return;
    switch (type) {
        case "client_id":
            payloadCallback["_google-clientID"] = clientId;
            break;
        case "session_id":
            payloadCallback["_ga4session-clientID"] = clientId;
            break;
        default:
            break;
    }
};
exports.storeGaIds = storeGaIds;
/**
 * Checks if GA4 session has expired and if it is
 * newer in GA cookie than in LD localStorage.
 * @param ga4MeasurementId GA4 Measurement ID
 * @returns {Promise<boolean | string>} promise returns new session_id or false
 */
const checkIfGaSessionIdNewer = async (ga4MeasurementId) => {
    const getStoredIds = (0, ldLocalStorage_1.getLDLocalStorage)();
    const storedGa4SessionId = getStoredIds === null || getStoredIds === void 0 ? void 0 : getStoredIds["_ga4session-clientID"];
    if (!ga4MeasurementId || !storedGa4SessionId) {
        return false;
    }
    const latestGa4SessionId = await (0, exports.getGaIds)(ga4MeasurementId, "session_id");
    return latestGa4SessionId !== storedGa4SessionId ? latestGa4SessionId : false;
};
exports.checkIfGaSessionIdNewer = checkIfGaSessionIdNewer;
/**
 * Fetches clientID or sessionID from GA3 or GA4.
 * Tries to retrieve from `gtag` first. If `gtag` is undefined, tries to get from cookies.
 * @param {string | undefined} id - GA3 Property ID or GA4 Measurement ID
 * @param {string} type - type of ID we want to retrieve (session or client)
 * @returns {Promise<string | null>} promise resolved with clientID or sessionID, or null
 */
const getGaIds = async (id, type) => {
    if (!id) {
        return null;
    }
    let gaId = null;
    if (typeof gtag !== "undefined") {
        gaId = await getGaIdFromGtag(id, type);
    }
    if (!gaId) {
        gaId = getGaIdFromCookies(id, type);
    }
    return gaId;
};
exports.getGaIds = getGaIds;
const getGaIdFromGtag = (id, type) => {
    const getPromse = new Promise((resolve) => {
        try {
            gtag("get", id, type, (clientId) => {
                resolve(clientId);
            });
        }
        catch (error) {
            resolve(null); // In case gtag fails
        }
    });
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
            resolve(null);
        }, 300); // Wait for 1 second before returning null if not found
    });
    return Promise.race([getPromse, timeoutPromise]);
};
/**
 * Helper function to extract GA client or session ID from cookies.
 * @param {string} id - GA4 Measurement ID
 * @param {string} type - Type of ID we want to retrieve (client_id or session_id)
 * @returns {string | null} the extracted ID or null if not found
 */
const getGaIdFromCookies = (id, type) => {
    var _a, _b;
    const getCookie = (name) => {
        const cookies = document.cookie.split(";").map((cookie) => cookie.trim());
        const foundCookie = cookies.find((cookie) => cookie.startsWith(`${name}=`));
        return foundCookie ? foundCookie.split("=")[1] : null;
    };
    if (type === "client_id") {
        const gaCookie = getCookie("_ga");
        return gaCookie ? gaCookie.split(".").slice(-2).join(".") : null;
    }
    else if (type === "session_id") {
        // This is the updated approach to get the session cookie
        const cookieName = `_ga_${id.replace("G-", "")}`;
        const sessionIDCookie = getCookie(cookieName);
        const sessionID = (_b = (_a = sessionIDCookie === null || sessionIDCookie === void 0 ? void 0 : sessionIDCookie.split) === null || _a === void 0 ? void 0 : _a.call(sessionIDCookie, ".")) === null || _b === void 0 ? void 0 : _b[2];
        return sessionID || null;
    }
    return null;
};
